/**
 * FileMakerBridge.ts - FileMaker WebViewer integration for Integratr
 * 
 * CHANGE HISTORY:
 * - 251031_2135 [pending] danielbeer: Initial creation for FMwvTool v4
 * 
 * Provides FileMaker-specific bridge methods for WebViewer integration
 */

export interface FileMakerEnvironment {
  platform: 'mac' | 'windows' | 'webdirect' | 'unknown';
  version: string;
  isWebDirect: boolean;
  isFileMaker: boolean;
}

export interface FileMakerScriptResult {
  success: boolean;
  result?: any;
  error?: string;
}

export class FileMakerBridge {
  private callbackHandlers: Map<string, (result: any) => void> = new Map();
  private callbackCounter: number = 0;

  /**
   * Check if running in FileMaker WebViewer
   */
  isFileMaker(): boolean {
    return typeof (window as any).FileMaker !== 'undefined';
  }

  /**
   * Get FileMaker environment information
   */
  getEnvironment(): FileMakerEnvironment {
    const userAgent = navigator.userAgent.toLowerCase();
    const isWebDirect = userAgent.includes('filemaker webdirect');
    const isMac = userAgent.includes('mac');
    const isWindows = userAgent.includes('win');

    // Try to detect FileMaker version
    let version = 'unknown';
    const versionMatch = userAgent.match(/filemaker\/(\d+\.\d+)/i);
    if (versionMatch) {
      version = versionMatch[1];
    }

    return {
      platform: isWebDirect ? 'webdirect' : isMac ? 'mac' : isWindows ? 'windows' : 'unknown',
      version,
      isWebDirect,
      isFileMaker: this.isFileMaker()
    };
  }

  /**
   * Call FileMaker script (fire and forget)
   */
  callScript(scriptName: string, parameter?: any): void {
    if (!this.isFileMaker()) {
      console.warn('Not running in FileMaker - script call ignored:', scriptName);
      return;
    }

    const param = parameter !== undefined ? JSON.stringify(parameter) : '';
    (window as any).FileMaker.PerformScript(scriptName, param);
  }

  /**
   * Call FileMaker script and wait for result
   */
  async callScriptWithResult(
    scriptName: string,
    parameter?: any,
    timeout: number = 5000
  ): Promise<FileMakerScriptResult> {
    if (!this.isFileMaker()) {
      return {
        success: false,
        error: 'Not running in FileMaker'
      };
    }

    return new Promise((resolve) => {
      const callbackId = `fm_callback_${this.callbackCounter++}`;
      const timeoutId = setTimeout(() => {
        this.callbackHandlers.delete(callbackId);
        resolve({
          success: false,
          error: 'Script call timed out'
        });
      }, timeout);

      // Register callback handler
      this.callbackHandlers.set(callbackId, (result: any) => {
        clearTimeout(timeoutId);
        this.callbackHandlers.delete(callbackId);
        resolve({
          success: true,
          result
        });
      });

      // Register global callback function
      (window as any)[callbackId] = (result: any) => {
        const handler = this.callbackHandlers.get(callbackId);
        if (handler) {
          handler(result);
        }
      };

      // Call script with callback ID
      const param = {
        ...parameter,
        _callbackId: callbackId
      };
      (window as any).FileMaker.PerformScript(scriptName, JSON.stringify(param));
    });
  }

  /**
   * Register JavaScript function callable from FileMaker
   */
  registerFunction(name: string, handler: Function): void {
    (window as any)[name] = (...args: any[]) => {
      try {
        return handler(...args);
      } catch (error) {
        console.error(`Error in FileMaker function ${name}:`, error);
        throw error;
      }
    };
  }

  /**
   * Batch register multiple functions
   */
  registerFunctions(functions: Record<string, Function>): void {
    Object.entries(functions).forEach(([name, handler]) => {
      this.registerFunction(name, handler);
    });
  }

  /**
   * Unregister function
   */
  unregisterFunction(name: string): void {
    delete (window as any)[name];
  }

  /**
   * Get value from FileMaker field (requires FileMaker 19+)
   */
  async getFieldValue(fieldName: string): Promise<string | null> {
    if (!this.isFileMaker()) {
      return null;
    }

    // This requires FileMaker 19+ with JavaScript integration
    if (typeof (window as any).FileMaker.PerformScriptWithOption === 'function') {
      const result = await this.callScriptWithResult('Get Field Value', { fieldName });
      return result.success ? result.result : null;
    }

    console.warn('getFieldValue requires FileMaker 19+ with JavaScript integration');
    return null;
  }

  /**
   * Set value in FileMaker field (requires FileMaker 19+)
   */
  async setFieldValue(fieldName: string, value: any): Promise<boolean> {
    if (!this.isFileMaker()) {
      return false;
    }

    if (typeof (window as any).FileMaker.PerformScriptWithOption === 'function') {
      const result = await this.callScriptWithResult('Set Field Value', { fieldName, value });
      return result.success;
    }

    console.warn('setFieldValue requires FileMaker 19+ with JavaScript integration');
    return false;
  }

  /**
   * Execute FileMaker script with options (FileMaker 19+)
   */
  async executeScriptWithOption(
    scriptName: string,
    parameter?: any,
    option?: number
  ): Promise<FileMakerScriptResult> {
    if (!this.isFileMaker()) {
      return {
        success: false,
        error: 'Not running in FileMaker'
      };
    }

    if (typeof (window as any).FileMaker.PerformScriptWithOption !== 'function') {
      return {
        success: false,
        error: 'PerformScriptWithOption requires FileMaker 19+'
      };
    }

    try {
      const param = parameter !== undefined ? JSON.stringify(parameter) : '';
      const result = (window as any).FileMaker.PerformScriptWithOption(
        scriptName,
        param,
        option || 0
      );

      return {
        success: true,
        result
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if FileMaker supports JavaScript integration (19+)
   */
  supportsJavaScriptIntegration(): boolean {
    return (
      this.isFileMaker() &&
      typeof (window as any).FileMaker.PerformScriptWithOption === 'function'
    );
  }

  /**
   * Get FileMaker capabilities
   */
  getCapabilities(): {
    basicScripting: boolean;
    scriptWithResult: boolean;
    javascriptIntegration: boolean;
    fieldAccess: boolean;
  } {
    const fm = (window as any).FileMaker;
    return {
      basicScripting: this.isFileMaker(),
      scriptWithResult: this.isFileMaker(),
      javascriptIntegration: this.supportsJavaScriptIntegration(),
      fieldAccess: this.supportsJavaScriptIntegration()
    };
  }

  /**
   * Clean up all registered callbacks and functions
   */
  cleanup(): void {
    // Clear all callback handlers
    this.callbackHandlers.forEach((_, callbackId) => {
      delete (window as any)[callbackId];
    });
    this.callbackHandlers.clear();
  }
}

// Singleton instance
let fileMakerBridgeInstance: FileMakerBridge | null = null;

export function getFileMakerBridge(): FileMakerBridge {
  if (!fileMakerBridgeInstance) {
    fileMakerBridgeInstance = new FileMakerBridge();
  }
  return fileMakerBridgeInstance;
}
